/**
 * VideoEmbed — the non-native video surface: a YouTube/Vimeo iframe, a plain
 * <video> for a direct file URL, or an "open in a new tab" card for anything
 * an external platform refuses to embed.
 *
 * Split out of ContentReviewPanel.tsx unchanged; the panel reaches it through
 * VideoSurface, which picks between this and the native PlayerColumn.
 */
import { ExternalLink } from '../../../icons';

/** YouTube Shorts are portrait-only, so the stage is pinned at 9:16. */
const SHORTS_WIDTH_PX = 315;
const SHORTS_HEIGHT_PX = 560;
const EXTERNAL_ICON_SIZE = 24;
const EXTERNAL_LINK_ICON_SIZE = 16;

const YOUTUBE_ID_PATTERN =
  /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
const YOUTUBE_SHORTS_PATTERN = /youtube\.com\/shorts\//;
const VIMEO_ID_PATTERN =
  /vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/forum\/topic:|)(\d+)/;
const DIRECT_VIDEO_PATTERN = /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i;
const BLOB_URL_PREFIX = 'blob:';

const IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
const VIMEO_IFRAME_ALLOW = 'autoplay; fullscreen; picture-in-picture';

const YOUTUBE_EMBED_BASE_URL = 'https://www.youtube.com/embed/';
const VIMEO_EMBED_BASE_URL = 'https://player.vimeo.com/video/';

const EXTERNAL_PREVIEW_TITLE = 'External Content Preview';
const EXTERNAL_PREVIEW_BODY = 'This content is hosted on an external platform that restricts direct embedding.';
const OPEN_EXTERNAL_LABEL = 'Open Link in New Tab';

function youtubeEmbedUrl(videoId: string): string {
  return `${YOUTUBE_EMBED_BASE_URL}${videoId}`;
}

function vimeoEmbedUrl(videoId: string): string {
  return `${VIMEO_EMBED_BASE_URL}${videoId}`;
}

export function youtubeEmbedId(url: string): string | null {
  const m = url.match(YOUTUBE_ID_PATTERN);
  return m?.[1] ?? null;
}

export function isYouTubeShorts(url: string): boolean {
  return YOUTUBE_SHORTS_PATTERN.test(url);
}

export function vimeoEmbedId(url: string): string | null {
  const m = url.match(VIMEO_ID_PATTERN);
  return m?.[1] ?? null;
}

function isDirectVideoUrl(url: string): boolean {
  return DIRECT_VIDEO_PATTERN.test(url) || url.includes(BLOB_URL_PREFIX);
}

export function VideoEmbed({ url, title }: { url: string; title: string }) {
  const yid = youtubeEmbedId(url);
  if (yid) {
    const isShorts = isYouTubeShorts(url);
    return isShorts ? (
      <div className="flex w-full justify-center">
        <div className="overflow-hidden rounded-sm bg-black" style={{ width: SHORTS_WIDTH_PX, height: SHORTS_HEIGHT_PX }}>
          <iframe
            title={title}
            src={youtubeEmbedUrl(yid)}
            className="size-full"
            allow={IFRAME_ALLOW}
            allowFullScreen
          />
        </div>
      </div>
    ) : (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black">
        <iframe
          title={title}
          src={youtubeEmbedUrl(yid)}
          className="size-full"
          allow={IFRAME_ALLOW}
          allowFullScreen
        />
      </div>
    );
  }

  const vid = vimeoEmbedId(url);
  if (vid) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black">
        <iframe
          title={title}
          src={vimeoEmbedUrl(vid)}
          className="size-full"
          allow={VIMEO_IFRAME_ALLOW}
          allowFullScreen
        />
      </div>
    );
  }

  if (isDirectVideoUrl(url)) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-sm bg-black ring-1 ring-white/10">
        <video src={url} controls className="size-full" title={title} controlsList="nodownload" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 overflow-hidden rounded-xl border border-border bg-muted p-8 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-card text-muted-foreground ring-1 ring-ring">
        <ExternalLink size={EXTERNAL_ICON_SIZE} />
      </div>
      <div className="max-w-[240px] space-y-1.5">
        <h4 className="text-body-sm-semibold text-foreground">{EXTERNAL_PREVIEW_TITLE}</h4>
        <p className="text-caption-regular leading-relaxed text-muted-foreground">{EXTERNAL_PREVIEW_BODY}</p>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex items-center gap-2 rounded-xl bg-(--brand-500) px-6 py-2.5 text-body-sm-semibold text-white transition-all hover:bg-(--brand-600)"
      >
        <ExternalLink size={EXTERNAL_LINK_ICON_SIZE} /> {OPEN_EXTERNAL_LABEL}
      </a>
    </div>
  );
}
